document.documentElement.classList.remove('no-js');

const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

function revealAll(root = document) {
  root.querySelectorAll('[data-reveal]').forEach((el) => el.setAttribute('data-revealed', ''));
}

function initReveals(root = document) {
  if (reducedMotion.matches) return revealAll(root);

  const targets = root.querySelectorAll('[data-reveal]:not([data-revealed])');
  if (!targets.length) return;

  const observer = new IntersectionObserver(
    (entries, obs) => {
      // A section arrives as one thing. Stagger is opt-in per element via
      // --sv-stagger-step; left unset, everything in a batch fades together.
      entries
        .filter((entry) => entry.isIntersecting)
        .forEach((entry, index) => {
          const step = Number(getComputedStyle(entry.target).getPropertyValue('--sv-stagger-step')) || 0;
          entry.target.style.setProperty('--sv-reveal-delay', `${index * step}ms`);
          entry.target.setAttribute('data-revealed', '');
          obs.unobserve(entry.target);
        });
    },
    { rootMargin: '0px 0px -12% 0px', threshold: 0.15 }
  );

  targets.forEach((el) => observer.observe(el));
}

initReveals();

reducedMotion.addEventListener('change', (event) => {
  if (event.matches) revealAll();
});

// Theme editor adds and removes sections live.
document.addEventListener('shopify:section:load', (event) => initReveals(event.target));

// ---------- Palette ----------

const MODE_KEY = 'sv-mode';

function setMode(mode) {
  const root = document.documentElement;

  if (mode === 'men') {
    root.setAttribute('data-mode', 'men');
  } else {
    root.removeAttribute('data-mode');
  }

  document.querySelectorAll('[data-mode-toggle]').forEach((button) => {
    button.setAttribute('aria-pressed', mode === 'men' ? 'true' : 'false');
  });

  try {
    if (mode === 'men') {
      localStorage.setItem(MODE_KEY, 'men');
    } else {
      localStorage.removeItem(MODE_KEY);
    }
  } catch (error) {
    // Private browsing: the palette still applies, it just will not persist.
  }
}

function initMode() {
  // The inline head script has already applied the stored palette; this only
  // catches the buttons up and wires them.
  setMode(document.documentElement.getAttribute('data-mode') === 'men' ? 'men' : 'light');

  document.querySelectorAll('[data-mode-toggle]').forEach((button) => {
    if (button.dataset.modeBound) return;
    button.dataset.modeBound = 'true';
    button.addEventListener('click', () => {
      setMode(document.documentElement.getAttribute('data-mode') === 'men' ? 'light' : 'men');
    });
  });
}

initMode();

// The theme editor rebuilds the header as you edit it.
document.addEventListener('shopify:section:load', initMode);

// ---------- Evidence picker ----------

function initEvidenceIndex() {
  const index = document.querySelector('[data-ev-index]');
  if (!index) return;

  const links = [...index.querySelectorAll('a[href^="#"]')];
  const entries = links
    .map((link) => document.getElementById(decodeURIComponent(link.hash.slice(1))))
    .filter(Boolean);
  if (!entries.length) return;

  const mark = (id) => {
    links.forEach((link) => {
      const current = decodeURIComponent(link.hash.slice(1)) === id;
      if (current) {
        link.setAttribute('aria-current', 'true');
      } else {
        link.removeAttribute('aria-current');
      }
    });
  };

  const observer = new IntersectionObserver(
    (records) => {
      // The entry nearest the top of the readable area wins, so the marker
      // never flickers between two that are both on screen.
      const visible = records
        .filter((record) => record.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
      if (visible) mark(visible.target.id);
    },
    { rootMargin: '-20% 0px -70% 0px', threshold: 0 }
  );

  entries.forEach((entry) => observer.observe(entry));
}

initEvidenceIndex();

document.addEventListener('shopify:section:load', initEvidenceIndex);

// ---------- Mobile navigation ----------

function initNav() {
  const header = document.querySelector('[data-site-header]');
  const button = header?.querySelector('[data-nav-toggle]');
  if (!header || !button || button.dataset.navBound) return;

  button.dataset.navBound = 'true';

  const close = () => {
    header.removeAttribute('data-nav-open');
    button.setAttribute('aria-expanded', 'false');
  };

  button.addEventListener('click', () => {
    const open = header.hasAttribute('data-nav-open');
    if (open) {
      close();
    } else {
      header.setAttribute('data-nav-open', '');
      button.setAttribute('aria-expanded', 'true');
    }
  });

  // A tap on a link, Escape, or anything outside the bar puts it away.
  header.querySelectorAll('.site-header__menu a').forEach((link) => {
    link.addEventListener('click', close);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') close();
  });

  document.addEventListener('click', (event) => {
    if (!header.contains(event.target)) close();
  });
}

initNav();

document.addEventListener('shopify:section:load', initNav);


// ---------- The product page ----------

function initPdp(root = document) {
  const form = root.querySelector('.pdp__form');

  if (form && !form.dataset.pdpBound) {
    form.dataset.pdpBound = 'true';
    const qty = form.querySelector('.pdp__qty-input');

    form.querySelector('[data-qty-down]')?.addEventListener('click', () => {
      qty.value = Math.max(1, Number(qty.value || 1) - 1);
    });

    form.querySelector('[data-qty-up]')?.addEventListener('click', () => {
      qty.value = Number(qty.value || 1) + 1;
    });

    // Variant ids live on the page already, so picking an option is a lookup
    // rather than a request. The price and the button follow the same
    // lookup, so neither can fall out of sync with what was actually picked.
    const variants = window.__pdpVariants;
    const copy = window.__pdpCopy;
    if (variants) {
      const applyVariant = (match) => {
        if (!match) return;
        form.querySelector('[data-variant-id]').value = match.id;

        document.querySelectorAll('[data-pdp-price]').forEach((el) => { el.textContent = match.price; });
        document.querySelectorAll('[data-pdp-sticky-price]').forEach((el) => { el.textContent = match.price; });

        const label = match.available ? copy?.cta : copy?.soldOut;
        document.querySelectorAll('[data-pdp-add], [data-pdp-sticky-add]').forEach((btn) => {
          btn.disabled = !match.available;
          if (label) btn.textContent = label;
        });
        document.querySelectorAll('[data-pdp-buy], [data-pdp-sticky-buy]').forEach((btn) => {
          btn.disabled = !match.available;
        });
      };

      form.querySelectorAll('[data-option-input]').forEach((input) => {
        input.addEventListener('change', () => {
          const chosen = [...form.querySelectorAll('[data-option-input]:checked')].map((i) => i.value);
          const match = variants.find((v) => v.options.every((o, i) => o === chosen[i]));
          applyVariant(match);
        });
      });
    }
  }

  // The size pop-up: a native <dialog>, so focus, Esc and the backdrop
  // behave the way people expect without any extra work.
  const sizeDialog = root.querySelector('[data-size-dialog]');
  if (sizeDialog && !sizeDialog.dataset.sizeBound) {
    sizeDialog.dataset.sizeBound = 'true';
    root.querySelectorAll('[data-size-open]').forEach((btn) => {
      btn.addEventListener('click', () => sizeDialog.showModal());
    });
    sizeDialog.querySelectorAll('[data-size-close]').forEach((btn) => {
      btn.addEventListener('click', () => sizeDialog.close());
    });
    sizeDialog.addEventListener('click', (event) => {
      if (event.target === sizeDialog) sizeDialog.close();
    });
  }

  // The sticky bar shows whenever the real buttons are off screen — below
  // the fold on arrival, or scrolled past — and gets out of the way when they're in view.
  const bar = root.querySelector('[data-pdp-sticky]');
  // Without a product there is no button to watch, so the price block stands
  // in for it and the bar can still be judged.
  const add = root.querySelector('.pdp__add') || root.querySelector('.pdp__price');
  if (bar && add && !bar.dataset.pdpBound) {
    bar.dataset.pdpBound = 'true';
    bar.hidden = false;

    new IntersectionObserver(
      ([entry]) => bar.toggleAttribute('data-shown', !entry.isIntersecting),
      { threshold: 0 }
    ).observe(add);
  }
}

initPdp();

document.addEventListener('shopify:section:load', (event) => initPdp(event.target));

// ---------- The cart ----------

function initCart(root = document) {
  // The checkout bar appears once the real button has scrolled away.
  const bar = root.querySelector('[data-cart-sticky]');
  const checkout = root.querySelector('.ct__checkout');
  if (bar && checkout && !bar.dataset.cartBound) {
    bar.dataset.cartBound = 'true';
    bar.hidden = false;

    new IntersectionObserver(
      ([entry]) => bar.toggleAttribute('data-shown', !entry.isIntersecting),
      { threshold: 0 }
    ).observe(checkout);
  }

  // Shopify applies codes through its own route; the field just goes there.
  root.querySelectorAll('[data-discount-form]').forEach((form) => {
    if (form.dataset.cartBound) return;
    form.dataset.cartBound = 'true';

    form.addEventListener('submit', (event) => {
      const code = form.querySelector('input[name="code"]').value.trim();
      if (!code) return;
      event.preventDefault();
      window.location.href = `/discount/${encodeURIComponent(code)}?redirect=/cart`;
    });
  });
}

initCart();

document.addEventListener('shopify:section:load', (event) => initCart(event.target));

// ---------- Before and after ----------

function initCompare(root = document) {
  root.querySelectorAll('[data-compare]').forEach((frame) => {
    const input = frame.querySelector('[data-compare-input]');
    if (!input || input.dataset.compareBound) return;
    input.dataset.compareBound = 'true';

    const paint = () => frame.style.setProperty('--pos', `${input.value}%`);

    input.addEventListener('input', paint);
    // A tap anywhere in the frame moves the handle there, which is what
    // people try first.
    frame.addEventListener('pointerdown', (event) => {
      if (event.target === input) return;
      const { left, width } = frame.getBoundingClientRect();
      input.value = String(((event.clientX - left) / width) * 100);
      paint();
    });

    paint();
  });
}

initCompare();

document.addEventListener('shopify:section:load', (event) => initCompare(event.target));

/* ---------- Scroll storytelling: one sticky image+caption that repaints
   itself as each panel below crosses the middle of the screen ---------- */

function initDiffScroll(root = document) {
  root.querySelectorAll('[data-diff-scroll]').forEach((section) => {
    if (section.dataset.diffScrollBound) return;
    section.dataset.diffScrollBound = 'true';

    const scrolly = section.querySelector('.hm__diff-scrolly');
    const stage = section.querySelector('[data-diff-stage]');
    const panels = [...section.querySelectorAll('.hm__diff-panel')];
    if (!scrolly || !stage || !panels.length || typeof IntersectionObserver === 'undefined') return;

    const stageImg = stage.querySelector('[data-diff-stage-img]');
    const stageCaption = stage.querySelector('[data-diff-stage-caption]');
    const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
    // The pinned-stage pattern needs real vertical room to work with — on a
    // phone the tab bar alone (six stacked pills) can eat the viewport, so
    // it stays off there and every panel just shows in full instead.
    const roomForStage = matchMedia('(min-width: 48rem)');
    if (!roomForStage.matches) return;

    // Enhance only once JS is actually running: the stage appears, and the
    // full panels underneath become invisible (but still-sized) triggers.
    scrolly.classList.add('is-scrolly');
    stage.hidden = false;

    const paint = (panel) => {
      if (!panel) return;
      stageImg.src = panel.dataset.diffSrc;
      stageImg.srcset = panel.dataset.diffSrcset;
      stageImg.alt = panel.dataset.diffAlt;
      stageCaption.textContent = panel.dataset.diffCaption;

      const radioId = panel.id.replace('diff-panel-', 'diff-');
      const radio = section.querySelector(`#${CSS.escape(radioId)}`);
      if (radio) radio.checked = true;
    };

    // Clicking a tab jumps straight to its panel, which then repaints the
    // stage the same way scrolling past it would.
    section.querySelectorAll('.hm__diff-tab').forEach((tab) => {
      tab.addEventListener('click', () => {
        const panel = section.querySelector(`#diff-panel-${tab.htmlFor.slice(5)}`);
        if (panel) panel.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
      });
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) paint(entry.target);
        });
      },
      { rootMargin: '-45% 0px -45% 0px', threshold: 0 }
    );

    panels.forEach((panel) => observer.observe(panel));
  });
}

initDiffScroll();

document.addEventListener('shopify:section:load', (event) => initDiffScroll(event.target));

// ---------- The bar over the hero ----------

let headerObserver;

function initHeader() {
  const header = document.querySelector('[data-site-header]');
  if (!header) return;

  headerObserver?.disconnect();

  const hero = document.querySelector('.hero, .hs');
  // Off the homepage, and on a phone where the hero sits below the bar rather
  // than under it, the bar is always solid.
  if (!hero || matchMedia('(max-width: 47.9375rem)').matches) {
    header.classList.add('is-solid');
    return;
  }

  header.classList.remove('is-solid');
  headerObserver = new IntersectionObserver(
    ([entry]) => header.classList.toggle('is-solid', !entry.isIntersecting),
    // Shrink the root by the bar's height, so the swap lands as the hero's
    // bottom edge passes under it.
    { rootMargin: `-${header.offsetHeight}px 0px 0px 0px`, threshold: 0 }
  );
  headerObserver.observe(hero);
}

initHeader();

document.addEventListener('shopify:section:load', () => initHeader());

// ---------- Menus with children ----------

function initMenus(root = document) {
  root.querySelectorAll('[data-menu-group]').forEach((group) => {
    const trigger = group.querySelector('[data-menu-trigger]');
    if (!trigger || trigger.dataset.menuBound) return;
    trigger.dataset.menuBound = 'true';

    const close = () => {
      group.removeAttribute('data-open');
      trigger.setAttribute('aria-expanded', 'false');
    };

    trigger.addEventListener('click', () => {
      const open = group.hasAttribute('data-open');
      // Only one panel hangs off the bar at a time.
      document.querySelectorAll('[data-menu-group][data-open]').forEach((other) => {
        if (other !== group) {
          other.removeAttribute('data-open');
          other.querySelector('[data-menu-trigger]')?.setAttribute('aria-expanded', 'false');
        }
      });
      group.toggleAttribute('data-open', !open);
      trigger.setAttribute('aria-expanded', String(!open));
    });

    group.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && group.hasAttribute('data-open')) {
        close();
        trigger.focus();
      }
    });

    // Clicking or tabbing away closes it.
    document.addEventListener('click', (event) => {
      if (!group.contains(event.target)) close();
    });

    group.addEventListener('focusout', (event) => {
      if (!group.contains(event.relatedTarget)) close();
    });
  });
}

initMenus();

document.addEventListener('shopify:section:load', (event) => initMenus(event.target));

// ---------- Preloader ----------

/* ---------- Comparison rows, as dropdowns on a phone ---------- */

function initTableAccordion(root = document) {
  const table = root.querySelector ? root.querySelector('.hm__table') : null;
  if (!table || table.dataset.accordionReady) return;
  table.dataset.accordionReady = '';

  const rows = [...table.tBodies[0].rows];
  const phone = matchMedia('(max-width: 47.9375rem)');

  const build = () => {
    table.classList.add('is-accordion');
    rows.forEach((tr) => {
      const th = tr.querySelector('th');
      if (!th || th.querySelector('button')) return;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'hm__acc';
      button.setAttribute('aria-expanded', 'false');
      button.textContent = th.textContent.trim();
      th.textContent = '';
      th.appendChild(button);
      button.addEventListener('click', () => {
        const open = tr.classList.toggle('is-open');
        button.setAttribute('aria-expanded', String(open));
      });
    });
  };

  const teardown = () => {
    table.classList.remove('is-accordion');
    rows.forEach((tr) => {
      const th = tr.querySelector('th');
      const button = th && th.querySelector('button');
      if (button) th.textContent = button.textContent;
      tr.classList.remove('is-open');
    });
  };

  const sync = () => (phone.matches ? build() : teardown());
  sync();
  phone.addEventListener('change', sync);
}

initTableAccordion();

document.addEventListener('shopify:section:load', (event) => initTableAccordion(event.target));


function initPreloader() {
  const el = document.querySelector('[data-preloader]');
  if (!el) return;

  let seen = false;
  try {
    seen = sessionStorage.getItem('sv-preloader') === '1';
  } catch (error) {
    // Private mode or blocked storage: treat it as a first visit.
  }

  if (seen) {
    el.remove();
    return;
  }

  const dismiss = () => {
    try {
      sessionStorage.setItem('sv-preloader', '1');
    } catch (error) {
      // Nothing to do; the preloader still goes.
    }
    el.setAttribute('data-done', '');
    setTimeout(() => el.remove(), 700);
  };

  const loaded = new Promise((resolve) => {
    if (document.readyState === 'complete') resolve();
    else addEventListener('load', resolve, { once: true });
  });
  // A floor so it never flashes, and a ceiling so a stalled asset never traps it.
  const floor = new Promise((resolve) => setTimeout(resolve, 900));
  const ceiling = new Promise((resolve) => setTimeout(resolve, 3500));

  Promise.race([Promise.all([loaded, floor]), ceiling]).then(dismiss);
}

initPreloader();
